import { inject, Injectable } from '@angular/core'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { GbTeamCreateTeamDialogComponent } from 'libs/gb-dashboard/team/feature/src/lib/gb-team-create-team-dialog/gb-team-create-team-dialog.component'
import { GbTeamProfilesDialogComponent } from 'libs/gb-dashboard/team/feature/src/lib/gb-team-profiles-dialog/gb-team-profiles-dialog.component'
import { catchError, concatMap, exhaustMap, finalize, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { TeamsApiService } from './teams-api.service'
import * as TeamsActions from './teams.actions'
import { TeamsFacade } from './teams.facade'
import { TeamsEntity } from './teams.models'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Injectable()
export class TeamsEffects {
  private actions$ = inject(Actions)

  loadTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadTeams),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.teamsFacade.allTeams$),
      exhaustMap(([action, urlName, allTeams]) => {
        const offset = action.shouldReset ? 0 : allTeams.length
        return this.teamsApiService.getTeams(urlName, offset, action.searchTerm, action.ordering || 'order').pipe(
          map((response) => TeamsActions.loadTeamsSuccess({ teams: response.results, count: response.count })),
          catchError((error) => of(TeamsActions.loadTeamsFailure({ error }))),
        )
      }),
    ),
  )

  createTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.createTeam),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.teamsApiService.createTeam(urlName, { ...action.team, auction: urlName }).pipe(
          map((team) => TeamsActions.createTeamSuccess({ team })),
          catchError((error) => of(TeamsActions.createTeamFailure({ error }))),
        ),
      ),
    ),
  )

  createTeamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.createTeamSuccess),
      tap(() => {
        this.gbNotificationService.success('TEAMS.CREATE_TEAMS_SUCCESS')
        this.gbDashboardDialogService.closeDialog(GbTeamCreateTeamDialogComponent)
      }),
      // TODO (GB-10789): should maintain search and ordering on reload
      map(() => TeamsActions.loadTeams({ shouldReset: true })),
    ),
  )

  updateTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.updateTeam),
      tap((action) => this.gbLoaderService.showLoaderDialog(action.type)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.teamsApiService.updateTeam(urlName, action.team).pipe(
          map((team) =>
            TeamsActions.updateTeamSuccess({
              team: { id: team.uuid, changes: team },
              onSuccess: action.onSuccess,
            }),
          ),
          catchError((error) => of(TeamsActions.updateTeamFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(action.type)),
        ),
      ),
    ),
  )

  updateTeamSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamsActions.updateTeamSuccess),
        tap((action) => {
          this.gbNotificationService.success('TEAMS.UPDATE_TEAM_SUCCESS')
          action.onSuccess?.()
        }),
      ),
    { dispatch: false },
  )

  deleteTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.deleteTeam),
      tap((action) => this.gbLoaderService.showLoaderDialog(action.type)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.teamsApiService.deleteTeam(urlName, action.team).pipe(
          map(() => TeamsActions.deleteTeamSuccess({ team: action.team })),
          catchError((error) => of(TeamsActions.deleteTeamFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(action.type)),
        ),
      ),
    ),
  )

  deleteTeamSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamsActions.deleteTeamSuccess),
        tap(() => this.gbNotificationService.success('TEAMS.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  )

  openTeamProfileDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamsActions.openTeamProfileDialog),
        tap((action) =>
          this.gbDashboardDialogService.getDialogObs(GbTeamProfilesDialogComponent, {
            disableClose: true,
            width: '90vw',
            data: { mode: action.mode },
          }),
        ),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          TeamsActions.loadTeamsFailure,
          TeamsActions.createTeamFailure,
          TeamsActions.deleteTeamFailure,
          TeamsActions.updateTeamFailure,
          TeamsActions.assignProfilesFailure,
          TeamsActions.removeProfilesFailure,
        ),
        tap((action) => {
          if (!action.error) return
          const errorMessage = this.gbErrorMessageService.getErrorMessage(action.error['error'])
          if (errorMessage && typeof errorMessage === 'string') this.gbNotificationService.error(errorMessage)
          else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  assignProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.assignProfiles),
      gbCheckLoaded(this.teamsFacade.selectedTeam$),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.teamsFacade.selectedTeam$),
      switchMap(([action, urlName, selectedTeam]) =>
        this.teamsApiService.assign(urlName, selectedTeam!.uuid, action.profiles).pipe(
          map(() => TeamsActions.assignProfilesSuccess()),
          catchError((error) => of(TeamsActions.assignProfilesFailure({ error }))),
        ),
      ),
    ),
  )

  removeProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.removeProfiles),
      gbCheckLoaded(this.teamsFacade.selectedTeam$),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.teamsFacade.selectedTeam$),
      switchMap(([action, urlName, selectedTeam]) =>
        this.teamsApiService.remove(urlName, selectedTeam!.uuid, action.profiles).pipe(
          map(() => TeamsActions.removeProfilesSuccess()),
          catchError((error) => of(TeamsActions.removeProfilesFailure({ error }))),
        ),
      ),
    ),
  )

  assignRemoveProfilesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamsActions.assignProfilesSuccess, TeamsActions.removeProfilesSuccess),
        tap(() => {
          this.gbNotificationService.success('TEAMS.UPDATE_TEAM_SUCCESS')
          this.gbDashboardDialogService.closeDialog(GbTeamProfilesDialogComponent)
        }),
      ),
    { dispatch: false },
  )

  reorderTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.reorderTeams),
      tap((action) => this.gbLoaderService.showLoaderDialog(action.type, 'LOADER.REORDERING_TEAMS')),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      concatMap(([action, urlName]) => {
        const apiCalls = action.teams.map((team) => this.teamsApiService.updateTeam(urlName, team))

        return forkJoin(apiCalls).pipe(
          switchMap((teams) => of(TeamsActions.reorderTeamsSuccess({ teams }))),
          catchError((error) => of(TeamsActions.reorderTeamsFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(action.type)),
        )
      }),
    ),
  )

  constructor(
    private teamsApiService: TeamsApiService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private teamsFacade: TeamsFacade,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
    private gbErrorMessageService: GbErrorMessageService,
  ) {}
}
